import { useEffect, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { Snackbar, Alert, Dialog, useMediaQuery, useTheme } from "@mui/material";
import axios from "axios";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import ListView from "./listview";
import MapView from "./mapview";
import BookingListLoader from "./BookingListLoader";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import PageHeader from "../../../components/PageHeader";
import ReassignBookingDriverPopup from "../../../components/popups/ReassignBookingDriverPopup";
import ViewSwitch from "../../../components/ListToolbar/ViewSwitch";
import { hasFeature } from "../../../components/Features";

// One list for every service: international bookings and local (Shop) deliveries, told apart by a tag on each row
const BookingList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "", date: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [viewType, setViewType] = useState('list');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const { websiteData } = useWebsite();
  useSiteBranding("Driver Jobs", websiteData);

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchBookings();
    } else {
      setPage(0);
    }
  }, [filterVars]);

  const fetchBookings = () => {
    axios.get(process.env.REACT_APP_API_URL + "v1/assigned-bookings", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        page: page + 1, // Laravel uses 1-based index
        per_page: rowsPerPage,
        search: filterVars.searchKey,
        start_date: filterVars.date ? dayjs(filterVars.date[0]).format("YYYY-MM-DD") : null,
        end_date: filterVars.date ? dayjs(filterVars.date[1]).format("YYYY-MM-DD") : null,
      }
    }).then((response) => {
      setContentLoading(false);

      setFilteredData(response.data.data.data); // actual rows
      setTotalCount(response.data.data.total); // total rows
    }).catch(() => {
      setContentLoading(false);
    });
  }

  const viewSwitch = hasFeature("route_optimization") ? <ViewSwitch value={viewType} onChange={setViewType} /> : null;

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Bookings" trail={[{ label: "Driver Jobs" }]} />
      <div className={`${styles.MainCard}`}>

        {contentLoading && <BookingListLoader rows={rowsPerPage > 10 ? 8 : rowsPerPage} />}

        {/* The List / Map switch sits in each view's toolbar */}

        {!contentLoading && <>
          {viewType === 'list' && <ListView filteredData={filteredData} filterVars={filterVars} setSelectedId={setSelectedId.bind(this)} setFormOpen={setFormOpen.bind(this)} setFilterVars={setFilterVars.bind(this)} setViewType={setViewType.bind(this)} viewSwitch={viewSwitch} page={page} setPage={setPage.bind(this)} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage.bind(this)} totalCount={totalCount} />}
          {viewType === 'map' && hasFeature("route_optimization") && <MapView allData={filteredData} setViewType={setViewType.bind(this)} viewSwitch={viewSwitch} filterVars={filterVars} setFilterVars={setFilterVars.bind(this)} />}
        </>}
      </div>
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <ReassignBookingDriverPopup selectedId={selectedId} setLoading={setLoading.bind(this)} modalClose={setFormOpen.bind(this)} setAllData={setFilteredData.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} />
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: isMobile ? "center" : "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(BookingList);
